/**
 * WebSocket Events Interface
 * Defines all WebSocket event types and payloads for Company panel
 */

// Connection Events
export interface AuthenticatedEvent {
  userId: number;
  email: string;
  roleId: number;
  companyId: number | null;
  connectedAt: string;
  socketId: string;
}

export interface ConnectionEvent {
  socketId: string;
  timestamp: string;
  reason?: string;
}

// User Events
export interface UserCreatedEvent {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roleId: number;
  companyId?: number;
  isActive: boolean;
  createdAt: string;
}

export interface UserUpdatedEvent {
  id: number;
  changes: Partial<UserCreatedEvent>;
  updatedAt: string;
}

export interface UserDeletedEvent {
  id: number;
  deletedAt: string;
}

// User Settings Events
export interface UserSettingUpdatedEvent {
  id: number;
  userId: number;
  key: string;
  value: string | number | boolean | object;
  updatedAt: string;
}

// User Devices Events
export interface UserDeviceRegisteredEvent {
  id: number;
  userId: number;
  deviceKey: string;
  deviceType: string;
  isActive: boolean;
  registeredAt: string;
}

export interface UserDeviceDeactivatedEvent {
  id: number;
  userId: number;
  deviceKey: string;
  deactivatedAt: string;
}

// User Ratings Events
export interface UserRatingCreatedEvent {
  id: number;
  ratedUserId: number;
  ratingUserId: number;
  rating: number;
  comment?: string;
  createdAt: string;
}

// Notification Events
export interface NotificationEvent {
  id: string;
  userId: number;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
  persistent: boolean;
}

// Chat Events
export interface ChatMessageEvent {
  id: string;
  roomId: string;
  userId: number;           // Changed from senderId to match backend
  senderName: string;
  message: string;
  messageType: 'user' | 'service';  // Changed to match backend enum
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface TypingEvent {
  roomId: string;
  userId: number;
  userName: string;
  isTyping: boolean;
}

// System Events
export interface SystemAlertEvent {
  id: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  affectedUsers?: number[];
  timestamp: string;
}

// Payout Events
export interface PayoutRequestEventData {
  id: number;
  driverId?: number;
  companyId?: number;
  amount: number;
  status: string;
  oldStatus: string | null;
  processedAt: string | null;
  processedBy?: number | null;
  rejectionReason: string | null;
  notes: string | null;
  walletTransactionId?: number | null;
  requestedAt?: string;
}

// Order Search Escalation Events (WSS-002/WSS-009)
export interface SearchEscalatedEvent {
  orderId: number;
  companyId: number;
  fromLevel: number;
  toLevel: number;
  reason?: string;
  eventId?: string;
  timestamp: string;
}

// Payment Events (WSS-003)
export interface PaymentEventData {
  paymentId?: number;
  orderId?: number;
  amount: number | string;
  status: string;
  error?: string;
  errorCode?: string;
  timestamp?: string;
}

// WebSocket Event Union Type
export type WebSocketEvent =
  | { type: 'authenticated'; data: AuthenticatedEvent }
  | { type: 'connect'; data: ConnectionEvent }
  | { type: 'disconnect'; data: ConnectionEvent }
  | { type: 'user:created'; data: UserCreatedEvent }
  | { type: 'user:updated'; data: UserUpdatedEvent }
  | { type: 'user:deleted'; data: UserDeletedEvent }
  | { type: 'user:setting:updated'; data: UserSettingUpdatedEvent }
  | { type: 'user:device:registered'; data: UserDeviceRegisteredEvent }
  | { type: 'user:device:deactivated'; data: UserDeviceDeactivatedEvent }
  | { type: 'user:rating:created'; data: UserRatingCreatedEvent }
  | { type: 'notification'; data: NotificationEvent }
  | { type: 'chat:message'; data: ChatMessageEvent }
  | { type: 'chat:typing'; data: TypingEvent }
  | { type: 'system:alert'; data: SystemAlertEvent }
  | { type: 'payout-payout-requested'; data: PayoutRequestEventData }
  | { type: 'payout-payout-approved'; data: PayoutRequestEventData }
  | { type: 'payout-payout-rejected'; data: PayoutRequestEventData }
  | { type: 'payout-payout-cancelled'; data: PayoutRequestEventData }
  | { type: 'payout-payout-completed'; data: PayoutRequestEventData }
  | { type: 'payment-payment-success'; data: PaymentEventData }
  | { type: 'payment-payment-failed'; data: PaymentEventData }
  | { type: 'payment-dispatcher-payment-failed'; data: PaymentEventData };

// WebSocket Connection Status
export enum WebSocketStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

// WebSocket Configuration
export interface WebSocketConfig {
  url: string;
  namespace: string;
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  timeout: number;
  forceNew: boolean;
}